// src/pages/Dashboard.jsx
import React, { useState, useEffect } from 'react';
import { authFetch } from '../utils/authFetch';
import { eErroreDiRete } from '../utils/rete';
import { Link, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui';

//const API_BASE = 'http://localhost:3001/api';
const API_BASE = '/api';

export default function Dashboard() {
  const [previewImage, setPreviewImage] = useState(null);
  const [role, setRole] = useState(localStorage.getItem('role') || null);
  const [username, setUsername] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  // 🔔 Stato richieste gas
  const [gasRequests, setGasRequests] = useState([]);
  const [unreadGasCount, setUnreadGasCount] = useState(0);

  // 🔔 Stato notifiche generali (inclusi account bloccati)
  const [notifications, setNotifications] = useState([]);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);

  // Modal notifiche (gas + generali)
  const [showNotificationsModal, setShowNotificationsModal] = useState(false);

  const navigate = useNavigate();

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('role');
    localStorage.removeItem('profilePicture');
    localStorage.removeItem('mustChangePassword');
    setPreviewImage('/default-avatar.jpg');
    navigate('/login');
  };

  // Helper per mostrare i nomi utente in modo carino
  const formatUserLabel = (userObj) => {
    if (!userObj) return 'utente';
    const nome = userObj.nome || '';
    const cognome = userObj.cognome || '';
    const full = `${nome} ${cognome}`.trim();
    if (full) return full;
    if (userObj.username) return userObj.username;
    if (userObj.email) return userObj.email;
    return 'utente';
  };

  // ====== CARICAMENTO DATI ======

  // Richieste gas per l'utente loggato
  const loadGasRequests = async () => {
    if (localStorage.getItem('role') === 'cliente') return;
    try {
      const res = await authFetch(`${API_BASE}/gas/requests?target=me`);
      if (!res.ok) {
        return;
      }
      const data = await res.json();
      setGasRequests(data);
      setUnreadGasCount(data.length);
    } catch (error) {
      console.error('Errore nel recupero richieste gas:', error);
    }
  };

  // Notifiche generali non lette per ruolo / utente
  const loadNotifications = async () => {
    try {
      const res = await authFetch(
        `${API_BASE}/notifications?onlyUnread=true&limit=50`
      );
      if (!res.ok) {
        return;
      }
      const data = await res.json();
      setNotifications(data);
      setUnreadNotificationCount(data.length);
    } catch (error) {
      console.error('Errore nel recupero notifiche:', error);
    }
  };

  const fetchUserData = async () => {
    try {
      const res = await authFetch(`${API_BASE}/user`);

      if (res.ok) {
        const data = await res.json();

        setPreviewImage(
          data.profilePicture
            ? `${API_BASE}${data.profilePicture}`
            : '/default-avatar.jpg'
        );

        if (data.role) {
          setRole(data.role);
          localStorage.setItem('role', data.role);
        }

        if (data.username) {
          setUsername(data.username);
        }

        // Dopo aver caricato l'utente, carichiamo richieste gas e notifiche
        await loadGasRequests();
        await loadNotifications();
      } else {
        if (res.status === 401) logout();
        else setPreviewImage('/default-avatar.jpg');
      }
    } catch (error) {
      console.error('Errore fetch profilo:', error);
      // senza rete la fetch lancia: non e' un problema di credenziali,
      // la sessione deve restare valida
      if (!eErroreDiRete(error)) logout();
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 🔴 BLOCCO CLIENTI: redirect automatico alla dashboard cliente
  useEffect(() => {
    if (role === 'cliente') {
      navigate('/dashboard-cliente', { replace: true });
    }
  }, [role, navigate]);


  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('profilePicture', file);

    try {
      const res = await authFetch(`${API_BASE}/upload-profile`, {
        method: 'POST',
        body: formData,
      });

      if (res.ok) {
        const data = await res.json();
        const fullUrl = `${data.profilePicture}`;
        setPreviewImage(fullUrl);
        localStorage.setItem('profilePicture', fullUrl);
      } else if (res.status === 401) {
        logout();
      }
    } catch (error) {
      console.error('Upload error:', error);
      if (!eErroreDiRete(error)) logout();
    }
  };

  // Segna tutte le notifiche generali come lette
  const handleMarkAllNotificationsRead = async () => {
    try {
      const res = await authFetch(`${API_BASE}/notifications/read-all`, {
        method: 'PATCH',
      });
      if (res.ok) {
        setNotifications([]);
        setUnreadNotificationCount(0);
      }
    } catch (error) {
      console.error('Errore nel marcare le notifiche come lette:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-gray-100">
        <div className="text-gray-600 text-lg animate-pulse">
          Caricamento dashboard...
        </div>
      </div>
    );
  }

  const isManager =
    role === 'admin' || role === 'segreteria' || role === 'gestione';
  const isSecretariat = role === 'segreteria';
  const isAdminOrGestione = role === 'admin' || role === 'gestione';
  const isTecnico = role === 'tecnico';
  const canSeeCalendarManager = isAdminOrGestione || isSecretariat;
  const canSeeCalendarTecnico = isTecnico;
  const canSeeGasBox = isAdminOrGestione || isTecnico || isSecretariat;
  const canSeeIotModules =
    role === 'admin'; // || role === 'gestione' || role === 'segreteria';

  // 🔔 Notifiche specifiche di account bloccati
  const blockedNotifications = notifications.filter(
    (n) => n.type === 'user_blocked'
  );
  const blockedCount = blockedNotifications.length;

  const totalUnread = unreadGasCount + unreadNotificationCount;

  return (
    <div className="min-h-screen bg-gray-100 pb-10">
      <div className="max-w-4xl mx-auto px-4 pt-6">
        
      </div>

      {/* Header / Avatar */}
      <div className="flex flex-col items-center pt-10 px-4">
        <h1 className="text-2xl font-bold text-gray-800 mb-2 flex items-center gap-3">
          <span>Benvenuto nella tua dashboard</span>
          {/* 🔔 Badge notifiche (gas + generali, incluse user_blocked) */}
          <button
            type="button"
            onClick={() => setShowNotificationsModal(true)}
            className="relative inline-flex items-center justify-center w-9 h-9 rounded-full bg-white border border-gray-200 shadow-sm hover:bg-gray-50 transition"
            aria-label="Notifiche"
          >
            <span className="text-lg">🔔</span>
            {totalUnread > 0 && (
              <span className="absolute -top-1 -right-1 inline-flex items-center justify-center px-1.5 py-0.5 rounded-full bg-red-600 text-white text-[10px] font-bold">
                {totalUnread}
              </span>
            )}
          </button>
        </h1>
        <p className="text-sm text-gray-500 mb-6">
          {username && <span className="font-medium">{username}</span>}{' '}
          {role && (
            <span className="inline-flex items-center ml-2 px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700 uppercase">
              {role}
            </span>
          )}
        </p>

        <div className="relative w-32 h-32 rounded-full overflow-hidden shadow-lg group">
          <img
            src={previewImage || '/default-avatar.jpg'}
            alt="Foto profilo"
            className="object-cover w-full h-full"
            onError={(e) => {
              e.target.src = '/default-avatar.jpg';
            }}
          />
          <div className="absolute bottom-0 w-full text-center bg-black/60 text-white text-xs py-1 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
            <label htmlFor="upload" className="cursor-pointer">
              Modifica foto
            </label>
            <input
              id="upload"
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="hidden"
            />
          </div>
        </div>
      </div>

      {/* Contenuto dashboard */}
      <div className="max-w-5xl mx-auto mt-10 px-4 grid gap-6 md:grid-cols-2">
        {/* Gestione Utenti */}
        {isManager && (
          <Card className="shadow-md p-6 flex flex-col">
            <div className="flex items-start justify-between gap-2 mb-2">
              <h2 className="text-xl font-semibold text-gray-800">
                Gestione utenti
              </h2>
              {blockedCount > 0 && (
                <button
                  type="button"
                  onClick={() => setShowNotificationsModal(true)}
                  className="relative inline-flex items-center justify-center w-8 h-8 rounded-full bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 transition"
                  aria-label="Account bloccati"
                >
                  <span>🔔</span>
                  <span className="absolute -top-1 -right-1 inline-flex items-center justify-center px-1.5 py-0.5 rounded-full bg-red-600 text-white text-[10px] font-bold">
                    {blockedCount}
                  </span>
                </button>
              )}
            </div>
            <p className="text-sm text-gray-500 mb-1">
              Crea nuovi utenti, aggiorna ruoli e gestisci le credenziali del
              team.
            </p>
            {blockedCount > 0 && (
              <p className="text-xs text-red-600 mb-3">
                Ci sono <strong>{blockedCount}</strong> account bloccati in
                attesa di sblocco.
              </p>
            )}
            <div className="flex flex-wrap gap-3 mt-auto">
              <Link
                to="/aggiungi-utente"
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium transition"
              >
                Aggiungi utente
              </Link>
              <Link
                to="/rimuovi-utente"
                className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 text-sm font-medium transition"
              >
                Modifica / reset utenti
              </Link>
            </div>
          </Card>
        )}

        {/* Report interventi */}
                {/* Report interventi */}
        {!isSecretariat && (
          <Card className="shadow-md p-6 flex flex-col">
            <h2 className="text-xl font-semibold text-gray-800 mb-2">
              Report interventi
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              Compila i report di manutenzione e verifica partendo dalle
              lavorazioni assegnate o in modalità manuale.
            </p>
            <div className="mt-auto">
              <button
                type="button"
                onClick={() => navigate('/report-interventi')}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium transition"
              >
                Compila report
              </button>
            </div>
          </Card>
        )}


        {/* Lavorazioni */}
        {(isSecretariat || isAdminOrGestione) && (
          <Card className="shadow-md p-6 flex flex-col">
            <h2 className="text-xl font-semibold text-gray-800 mb-2">
              Lavorazioni
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              Assegna le lavorazioni giornaliere ai tecnici: definisci luogo,
              tipo intervento, durata e materiali necessari.
            </p>
            <div className="mt-auto">
              <button
                type="button"
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm font-medium transition"
                onClick={() => navigate('/lavorazioni')}
              >
                Assegna lavorazione
              </button>
            </div>
          </Card>
        )}

        {/* Calendario */}
        {(canSeeCalendarManager || canSeeCalendarTecnico) && (
          <Card className="shadow-md p-6 flex flex-col">
            <h2 className="text-xl font-semibold text-gray-800 mb-2">
              {canSeeCalendarManager
                ? 'Calendario aziendale'
                : 'Calendario personale'}
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              {canSeeCalendarManager
                ? 'Gestisci giustificativi di ferie e ROL e tieni aggiornate le giornate di chiusura aziendale.'
                : 'Consulta i giorni di chiusura aziendale e le tue ferie/ROL approvate.'}
            </p>
            <div className="mt-auto">
              {canSeeCalendarManager ? (
                <button
                  type="button"
                  onClick={() => navigate('/calendario')}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium transition"
                >
                  Gestisci calendario
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => navigate('/calendario-tecnico')}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium transition"
                >
                  Apri calendario
                </button>
              )}
            </div>
          </Card>
        )}

        {/* Fatturazione / Lavorazioni assegnate */}
        {isTecnico && (
          // 🔧 CARD SOLO PER TECNICI
          <Card className="shadow-md p-6 flex flex-col">
            <h2 className="text-xl font-semibold text-gray-800 mb-2">
              Lavorazioni assegnate
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              Visualizza tutte le lavorazioni che ti sono state assegnate e il
              calendario degli interventi programmati.
            </p>
            <div className="mt-auto">
              <button
                type="button"
                onClick={() => navigate('/lavorazioni/assegnate')}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm font-medium transition"
              >
                Vai alle lavorazioni assegnate
              </button>
            </div>
          </Card>
        )}

        {/* Timbrature: il tecnico timbra, i responsabili controllano */}
        {(isTecnico || isManager) && (
          <Card className="shadow-md p-6 flex flex-col">
            <h2 className="text-xl font-semibold text-gray-800 mb-2">
              {isTecnico ? 'Timbrature' : 'Timbrature del personale'}
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              {isTecnico
                ? 'Timbra entrata, pausa pranzo e uscita: la posizione viene registrata a ogni timbratura.'
                : 'Ore lavorate, pause, straordinari e posizioni delle timbrature dei tecnici.'}
            </p>
            <div className="mt-auto flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => navigate(isTecnico ? '/timbrature' : '/timbrature/gestione')}
                className="px-4 py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700 text-sm font-medium transition"
              >
                {isTecnico ? 'Timbra' : 'Controlla timbrature'}
              </button>
              {isManager && (
                <button
                  type="button"
                  onClick={() => navigate('/timbrature')}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium transition"
                >
                  Le mie timbrature
                </button>
              )}
            </div>
          </Card>
        )}

        {(role === 'admin' || role === 'gestione' || role === 'segreteria') && (
          // 💼 CARD FATTURAZIONE SOLO PER ADMIN / GESTIONE / SEGRETERIA
          <Card className="shadow-md p-6 flex flex-col">
            <h2 className="text-xl font-semibold text-gray-800 mb-2">
              Fatturazione
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              Gestisci Fatturazione Ciesse Impianti collegate alle diverse
              commesse.
            </p>
            <div className="mt-auto">
              <button
                type="button"
                onClick={() => navigate('/gestione-fatturazione')}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 text-sm font-medium transition"
              >
                Gestione fatturazione
              </button>
            </div>
          </Card>
        )}

        {(role === 'admin' || role === 'segreteria') && (
          <Card className="shadow-md p-6 flex flex-col">
            <h2 className="text-xl font-semibold text-gray-800 mb-2">
              Associa Cliente a lavorazioni
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              Collega lavorazioni già esistenti a un account cliente (record storici).
            </p>
            <div className="mt-auto">
              <button
                type="button"
                onClick={() => navigate('/cliente-associa')}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-medium transition"
              >
                Apri associazione cliente
              </button>
            </div>
          </Card>
        )}


        {isAdminOrGestione && (
          <Card className="shadow-md p-6 flex flex-col">
            <h2 className="text-xl font-semibold text-gray-800 mb-2">
              Dashboard contabile
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              Analizza fatturato, stato pagamenti e top clienti con grafici
              e indicatori dedicati.
            </p>
            <div className="mt-auto">
              <button
                type="button"
                onClick={() => navigate('/dashboard-contabile')}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-medium transition"
              >
                Apri dashboard contabile
              </button>
            </div>
          </Card>
        )}

        {isAdminOrGestione && (
          <Card className="shadow-md p-6 flex flex-col">
            <h2 className="text-xl font-semibold text-gray-800 mb-2">
              Mappa interventi e costi
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              Dove sono stati fatti interventi e manutenzioni, quanto sono
              costati in carburante, usura dei furgoni e ore, e la classifica
              mensile dei tecnici.
            </p>
            <div className="mt-auto">
              <button
                type="button"
                onClick={() => navigate('/mappa-interventi')}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-medium transition"
              >
                Apri mappa interventi
              </button>
            </div>
          </Card>
        )}


        {/* Gas */}
        {canSeeGasBox && (
          <Card className="shadow-md p-6 flex flex-col">
            <h2 className="text-xl font-semibold text-gray-800 mb-2">
              Livelli gas furgone
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              Visualizza e aggiorna i livelli di gas presenti nel furgone
              (R452A, R449A, R290, R170, R508B, R23, R600, R134A, R448A,
              ecc.). I dati sono condivisi con tutti gli altri ruoli tecnici.
            </p>
            <div className="mt-auto flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => navigate('/gas')}
                className="px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 text-sm font-medium transition"
              >
                Gestione livelli gas
              </button>
              {unreadGasCount > 0 && (
                <button
                  type="button"
                  onClick={() => setShowNotificationsModal(true)}
                  className="inline-flex items-center text-xs text-red-600 hover:text-red-700"
                >
                  <span className="mr-1">🔔</span>
                  <span>{unreadGasCount} richieste in attesa</span>
                </button>
              )}
            </div>
          </Card>
        )}




        {/* Da Clotho */}
        {canSeeIotModules && (
          <>
            <Card className="shadow-md p-6 flex flex-col">
              <h2 className="text-xl font-semibold text-gray-800 mb-2">
                Gestione macchine
              </h2>
              <p className="text-sm text-gray-500 mb-4">
                Crea e modifica le macchine monitorate, con dati relativi a sede,
                reparto, cliente e installazione.
              </p>
              <div className="mt-auto">
                <button
                  type="button"
                  onClick={() => navigate('/gestione-macchine')}
                  className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-800 text-sm font-medium transition"
                >
                  Apri gestione macchine
                </button>
              </div>
            </Card>

            <Card className="shadow-md p-6 flex flex-col">
              <h2 className="text-xl font-semibold text-gray-800 mb-2">
                Dispositivi smart
              </h2>
              <p className="text-sm text-gray-500 mb-4">
                Registra dispositivi, completa l’associazione impiantistica e
                verifica l’ultimo contatto ricevuto.
              </p>
              <div className="mt-auto">
                <button
                  type="button"
                  onClick={() => navigate('/dispositivi-smart')}
                  className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 text-sm font-medium transition"
                >
                  Apri dispositivi smart
                </button>
              </div>
            </Card>

            <Card className="shadow-md p-6 flex flex-col">
              <h2 className="text-xl font-semibold text-gray-800 mb-2">
                Telemetria real time
              </h2>
              <p className="text-sm text-gray-500 mb-4">
                Visualizza dati live per dispositivo, con filtri, dettaglio
                telemetrico e aggiornamento automatico.
              </p>
              <div className="mt-auto">
                <button
                  type="button"
                  onClick={() => navigate('/telemetria-real-time')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium transition"
                >
                  Apri telemetria
                </button>
              </div>
            </Card>

            <Card className="shadow-md p-6 flex flex-col">
              <h2 className="text-xl font-semibold text-gray-800 mb-2">
                Allarmi e soglie
              </h2>
              <p className="text-sm text-gray-500 mb-4">
                Definisci soglie di sicurezza, visualizza alert attivi e gestisci
                eventi di anomalia o offline.
              </p>
              <div className="mt-auto">
                <button
                  type="button"
                  onClick={() => navigate('/allarmi-soglie')}
                  className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm font-medium transition"
                >
                  Apri allarmi e soglie
                </button>
              </div>
            </Card>

            <Card className="shadow-md p-6 flex flex-col">
              <h2 className="text-xl font-semibold text-gray-800 mb-2">
                Storico letture
              </h2>
              <p className="text-sm text-gray-500 mb-4">
                Consulta lo storico delle misure per macchina, dispositivo,
                periodo e tipo di sensore.
              </p>
              <div className="mt-auto">
                <button
                  type="button"
                  onClick={() => navigate('/storico-letture')}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium transition"
                >
                  Apri storico letture
                </button>
              </div>
            </Card>
          </>
        )}

        {(canSeeIotModules || isTecnico) && (
          <Card className="shadow-md p-6 flex flex-col">
            <h2 className="text-xl font-semibold text-gray-800 mb-2">
              Aggiornamento firmware
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              Parco macchine con versione firmware installata, diagnostica dei
              device e invio dell&apos;aggiornamento OTA a uno o più dispositivi.
            </p>
            <div className="mt-auto">
              <button
                type="button"
                onClick={() => navigate('/aggiornamento-firmware')}
                className="px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 text-sm font-medium transition"
              >
                Apri aggiornamento firmware
              </button>
            </div>
          </Card>
        )}







        {/* Notifiche interventi + gas */}
        <Card className="shadow-md p-6 flex flex-col md:col-span-2">
          <h2 className="text-xl font-semibold text-gray-800 mb-2">
            Notifiche interventi, gas e utenti
          </h2>
          <p className="text-sm text-gray-500 mb-4">
            Qui vedrai le assegnazioni giornaliere (lavorazioni), le notifiche
            relative alle richieste di gas e gli avvisi sugli account bloccati.
          </p>

          {/* Notifiche generali sintetiche */}
          <div className="mt-2 text-sm text-gray-700 space-y-2">
            {notifications.length === 0 ? (
              <p className="text-gray-500">
                Nessuna nuova notifica generale al momento.
              </p>
            ) : (
              notifications.slice(0, 3).map((n) => {
                const isGasUsage = n.type === 'gas_usage';
                const isUserBlocked = n.type === 'user_blocked';
                const boxClasses = isUserBlocked
                  ? 'border-red-200 bg-red-50 text-red-900'
                  : 'border-amber-100 bg-amber-50 text-amber-900';

                return (
                  <div
                    key={n._id}
                    className={`border rounded-lg px-3 py-2 text-xs flex items-center justify-between ${boxClasses}`}
                  >
                    <span>
                      {isUserBlocked && n.user ? (
                        <>
                          Account{' '}
                          <span className="font-semibold">
                            {formatUserLabel(n.user)}
                          </span>{' '}
                          è stato <strong>bloccato</strong> per troppi
                          tentativi di accesso.
                        </>
                      ) : isGasUsage && n.createdBy ? (
                        <>
                          <span className="font-semibold">
                            {formatUserLabel(n.createdBy)}
                          </span>{' '}
                          {n.message}
                        </>
                      ) : (
                        n.message
                      )}
                    </span>
                  </div>
                );
              })
            )}
          </div>

          {/* Notifiche gas sintetiche */}
          <div className="mt-4 text-sm text-gray-700">
            {gasRequests.length === 0 ? (
              <p className="text-gray-500">
                Non hai richieste gas in sospeso al momento.
              </p>
            ) : (
              <div className="space-y-2">
                {gasRequests.slice(0, 3).map((req) => (
                  <div
                    key={req._id}
                    className="border border-blue-100 bg-blue-50 rounded-lg px-3 py-2 text-xs flex items-center justify-between"
                  >
                    <span className="text-blue-900">
                      Richiesta di{' '}
                      <span className="font-semibold">{req.gasName}</span> (
                      {req.amountGrams} g) da{' '}
                      <span className="font-semibold">
                        {formatUserLabel(req.fromUser)}
                      </span>
                      .
                    </span>
                    <button
                      type="button"
                      onClick={() => navigate('/gas')}
                      className="ml-2 px-2 py-1 bg-sky-600 text-white rounded text-[11px] hover:bg-sky-700"
                    >
                      Vai a gas
                    </button>
                  </div>
                ))}
                {gasRequests.length > 3 && (
                  <button
                    type="button"
                    onClick={() => setShowNotificationsModal(true)}
                    className="text-xs text-sky-700 hover:text-sky-800 mt-1"
                  >
                    Vedi tutte le richieste ({gasRequests.length})
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Testo esistente per interventi */}
          <div className="mt-4 text-sm text-gray-600">
            {isTecnico ? (
              <p className="mb-1">
                Gli interventi giornalieri e le nuove assegnazioni compariranno
                qui non appena verranno inseriti da segreteria o gestione.
              </p>
            ) : (
              <p className="mb-1">
                Come {role}, potrai in futuro creare e assegnare lavorazioni ai
                tecnici direttamente da questa sezione e collegarle al
                calendario aziendale.
              </p>
            )}
          </div>
        </Card>
      </div>

      {/* MODAL NOTIFICHE (gas + generali) */}
      {showNotificationsModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={() => setShowNotificationsModal(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 max-h-[80vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800">
                Notifiche
              </h3>
              <button
                type="button"
                onClick={() => setShowNotificationsModal(false)}
                className="text-gray-500 hover:text-gray-800 text-xl leading-none"
              >
                ×
              </button>
            </div>

            <div className="px-4 py-3 overflow-y-auto flex-1 space-y-4 text-sm">
              {/* Richieste gas */}
              <div>
                <h4 className="text-xs font-semibold text-blue-700 uppercase mb-1">
                  Richieste gas in sospeso
                </h4>
                {gasRequests.length === 0 ? (
                  <p className="text-xs text-gray-500">
                    Nessuna richiesta gas in questo momento.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {gasRequests.map((req) => (
                      <div
                        key={req._id}
                        className="border border-blue-100 bg-blue-50 rounded-lg px-3 py-2 text-xs"
                      >
                        <p className="text-blue-900">
                          <span className="font-semibold">
                            {formatUserLabel(req.fromUser)}
                          </span>{' '}
                          ha richiesto{' '}
                          <span className="font-semibold">
                            {req.amountGrams} g
                          </span>{' '}
                          di{' '}
                          <span className="font-semibold">
                            {req.gasName}
                          </span>
                          .
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Notifiche generali */}
              <div>
                <h4 className="text-xs font-semibold text-amber-700 uppercase mb-1">
                  Altre notifiche
                </h4>
                {notifications.length === 0 ? (
                  <p className="text-xs text-gray-500">
                    Nessuna notifica generale non letta.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {notifications.map((n) => {
                      const isGasUsage = n.type === 'gas_usage';
                      const isUserBlocked = n.type === 'user_blocked';
                      const containerClasses = isUserBlocked
                        ? 'border border-red-200 bg-red-50'
                        : 'border border-amber-100 bg-amber-50';
                      const textClasses = isUserBlocked
                        ? 'text-red-900'
                        : 'text-amber-900';

                      return (
                        <div
                          key={n._id}
                          className={`${containerClasses} rounded-lg px-3 py-2 text-xs`}
                        >
                          <p className={textClasses}>
                            {isUserBlocked && n.user ? (
                              <>
                                Account{' '}
                                <span className="font-semibold">
                                  {formatUserLabel(n.user)}
                                </span>{' '}
                                è stato <strong>bloccato</strong> per troppi
                                tentativi di accesso.
                              </>
                            ) : isGasUsage && n.createdBy ? (
                              <>
                                <span className="font-semibold">
                                  {formatUserLabel(n.createdBy)}
                                </span>{' '}
                                {n.message}
                              </>
                            ) : (
                              n.message
                            )}
                          </p>
                          {n.createdAt && (
                            <p className="mt-1 text-[10px] text-amber-700">
                              {new Date(n.createdAt).toLocaleString()}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="px-4 py-3 border-t flex justify-between items-center bg-gray-50">
              <button
                type="button"
                onClick={() => setShowNotificationsModal(false)}
                className="px-3 py-1.5 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100"
              >
                Chiudi
              </button>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleMarkAllNotificationsRead}
                  className="px-3 py-1.5 text-xs text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100"
                >
                  Segna notifiche come lette
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowNotificationsModal(false);
                    navigate('/gas');
                  }}
                  className="px-4 py-1.5 text-sm bg-sky-600 text-white rounded-lg hover:bg-sky-700"
                >
                  Vai alla pagina gas
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
